"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Sparkles, X } from "lucide-react";

import { Card, Section, Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { cn } from "@/lib/utils";

interface DefinitionMetric {
  value: string;
  label: React.ReactNode;
}

interface DefinitionSummaryCardProps {
  id?: string;
  sectionLabel?: string;
  title: React.ReactNode;
  paragraphs: React.ReactNode[];
  metrics: DefinitionMetric[];
  linkHref?: string;
  linkText?: string;
  className?: string;
  children?: React.ReactNode;
  /** Short, plain-language version of the paragraphs. When provided, an "AI Summary" toggle is shown. */
  aiSummary?: React.ReactNode[];
  /** Hides `children` until the AI summary is toggled on. */
  childrenOnSummaryOnly?: boolean;
}

export function DefinitionSummaryCard({
  id,
  sectionLabel,
  title,
  paragraphs,
  metrics,
  linkHref,
  linkText,
  className = "",
  children,
  aiSummary,
  childrenOnSummaryOnly = false,
}: DefinitionSummaryCardProps) {
  const [showSummary, setShowSummary] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const handleToggle = () => {
    if (showSummary) {
      // Collapse back to the full text immediately.
      setIsVisible(false);
      window.setTimeout(() => {
        setShowSummary(false);
        setIsVisible(true);
      }, 200);
      return;
    }

    // Simulate a brief "thinking" beat before revealing the condensed summary.
    setIsGenerating(true);
    window.setTimeout(() => {
      setIsGenerating(false);
      setIsVisible(false);
      window.setTimeout(() => {
        setShowSummary(true);
        setIsVisible(true);
      }, 200);
    }, 650);
  };

  return (
    <Section id={id} variant="default" background="gray" className={className}>
      <div className="max-w-4xl mx-auto">
        {sectionLabel && (
          <div className="text-center mb-4">
            <span className="section-label">{sectionLabel}</span>
          </div>
        )}
        <Card
          variant="default"
          padding="lg"
          className="relative rounded-2xl border border-gray-200 shadow-sm overflow-hidden bg-white"
        >
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="pl-4 border-l-4 border-secondary">
              <Typography variant="h5" as="h2" className="text-text-primary font-semibold">
                {title}
              </Typography>
            </div>

            {aiSummary && aiSummary.length > 0 && (
              <button
                type="button"
                onClick={handleToggle}
                disabled={isGenerating}
                aria-pressed={showSummary}
                className={cn(
                  "group shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-200",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40",
                  showSummary
                    ? "bg-secondary text-white border-secondary shadow-sm hover:bg-secondary/90"
                    : "bg-secondary/5 text-secondary border-secondary/20 hover:bg-secondary/10 hover:border-secondary/30"
                )}
              >
                {showSummary ? (
                  <>
                    <X className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Show Full</span>
                    <span className="sm:hidden">Full</span>
                  </>
                ) : (
                  <>
                    <Sparkles
                      className={cn(
                        "w-3.5 h-3.5 transition-transform duration-500",
                        isGenerating && "animate-pulse"
                      )}
                    />
                    <span className="hidden sm:inline">
                      {isGenerating ? "Summarizing…" : "AI Summary"}
                    </span>
                    <span className="sm:hidden">{isGenerating ? "…" : "AI"}</span>
                  </>
                )}
              </button>
            )}
          </div>

          <div
            className={cn(
              "transition-all duration-200 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
            )}
          >
            {showSummary ? (
              <div className="mb-6 rounded-xl border border-secondary/15 bg-secondary/5 p-4 sm:p-5">
                <div className="flex items-center gap-1.5 mb-2.5 text-secondary">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-semibold uppercase tracking-wide">
                    AI Summary
                  </span>
                </div>
                {aiSummary!.map((line, index) => (
                  <Typography
                    key={index}
                    variant="body"
                    as="p"
                    className="text-text-primary leading-5 text-[14px] flex gap-2 mb-1.5 last:mb-0"
                  >
                    <span className="mt-2 block w-1.5 h-1.5 rounded-full bg-secondary shrink-0" />
                    <span>{line}</span>
                  </Typography>
                ))}
              </div>
            ) : (
              paragraphs.map((paragraph, index) => (
                <Typography
                  key={index}
                  variant="small"
                  as="p"
                  className="text-text-secondary mb-2.5 text-[14px] leading-6 font-light last:mb-0"
                >
                  {paragraph}
                </Typography>
              ))
            )}
          </div>

          {childrenOnSummaryOnly && !showSummary ? null : children}

          {metrics.length > 0 ? (
            <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-3">
              {metrics.map(metric => (
                <div
                  key={metric.value + String(metric.label)}
                  className="flex items-stretch gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5"
                >
                  <Typography
                    variant="body"
                    as="p"
                    className="min-w-16 shrink-0 self-center whitespace-nowrap text-lg font-extrabold leading-none text-accent tabular-nums md:text-xl"
                  >
                    {metric.value}
                  </Typography>
                  <span className="w-px shrink-0 self-stretch bg-primary" aria-hidden="true" />
                  <Typography
                    variant="small"
                    as="p"
                    className="min-w-0 self-center text-[12px] leading-snug text-text-secondary"
                  >
                    {metric.label}
                  </Typography>
                </div>
              ))}
            </div>
          ) : null}

          {linkHref && linkText ? (
            <div className="mt-4 w-full text-center text-text-primary">
              <Link href={linkHref} rel="noreferrer" className="text-secondary">
                <NewButton variant="linkPrimary">
                {linkText}
                </NewButton>
              </Link>
            </div>
          ) : null}
        </Card>
      </div>
    </Section>
  );
}
